import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.jsx';
import { menuLeaves, canAccessLeaf, ROLES } from '../../config/menuData.js';
import api from '../../services/api.js';
import { money } from '../../utils/money.js';

const count = (v) => (Array.isArray(v) ? v.length : 0);
// Commercial totals carry `currency` (null when drafts mix currencies) and a
// per-currency split; never print one code over amounts in several.
const revenueText = (totals) => {
  if (totals.currency === null && Array.isArray(totals.revenueByCurrency) && totals.revenueByCurrency.length) {
    return totals.revenueByCurrency.map((r) => money(r.amount, r.currency, { decimals: 0 })).join(' · ');
  }
  return money(totals.totalRevenue || 0, totals.currency || 'USD', { decimals: 0 });
};
const settle = (p) => (p && typeof p.then === 'function' ? p.then((v) => v).catch(() => null) : Promise.resolve(null));
const arr = (v) => (Array.isArray(v) ? v : Array.isArray(v?.items) ? v.items : []);
// First defined value among several candidate keys (handles camelCase / snake_case).
const pick = (o, ...keys) => {
  for (const k of keys) if (o && o[k] != null && o[k] !== '') return o[k];
  return undefined;
};

/**
 * Role-aware landing dashboard. Each business role gets KPIs, a visual chart,
 * and a live worklist/queue table pulled from its own APIs, plus quick links to
 * its pages. Field roles (Captain/Pilot) see this inside the PWA shell.
 *
 * Each role loader returns { kpis, chart?, table? }:
 *   kpis  → [{ label, value, icon, tone, to }]
 *   chart → { title, bars: [{ label, value, pct, tone }] }
 *   table → { title, to, columns: [{ key, label, align }], rows: [...], empty }
 */

const ROLE_DASH = {
  [ROLES.ADMIN]: {
    accent: 'dark',
    blurb: 'Governance & configuration overview.',
    async load() {
      const [regs, users, audit] = await Promise.all([
        settle(api.listCustomerRegistrations?.()),
        settle(api.listUsers?.()),
        settle(api.searchAuditLogs({})),
      ]);
      const regRows = arr(regs);
      const pending = regRows.filter((r) => /pending/i.test(pick(r, 'status') || ''));
      return {
        kpis: [
          { label: 'Pending customer approvals', value: pending.length, icon: 'fa-user-shield', tone: 'warning', to: '/customer-approvals' },
          { label: 'User accounts', value: count(arr(users)), icon: 'fa-users-cog', tone: 'primary', to: '/user-accounts-roles-permissions-rbac' },
          { label: 'Audit ledger entries', value: count(arr(audit)), icon: 'fa-fingerprint', tone: 'secondary', to: '/immutable-audit-trails-logger' },
        ],
        table: {
          title: 'Customer registrations awaiting review',
          to: '/customer-approvals',
          columns: [
            { key: 'company', label: 'Company' },
            { key: 'contact', label: 'Contact' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: pending.slice(0, 6).map((r) => ({
            company: pick(r, 'companyName', 'company_name', 'company', 'name') || '—',
            contact: pick(r, 'contactEmail', 'contact_email', 'email', 'contactName') || '—',
            status: pick(r, 'status') || 'Pending',
          })),
          empty: 'No registrations awaiting review.',
        },
      };
    },
  },

  [ROLES.DISPATCHER]: {
    accent: 'primary',
    blurb: 'Live operations, fleet and dispatch.',
    async load() {
      const [m, exc, confirmed] = await Promise.all([
        settle(api.getOperationsMetrics?.('month')),
        settle(api.getExceptions?.()),
        settle(api.listConfirmedBookings?.()),
      ]);
      const util = arr(m?.utilization);
      const avg = util.length
        ? Math.round(util.reduce((a, u) => a + (pick(u, 'utilizationPct') || 0), 0) / util.length)
        : 0;
      const confirmedRows = arr(confirmed);
      return {
        kpis: [
          { label: 'Open exceptions / alarms', value: count(arr(exc)), icon: 'fa-triangle-exclamation', tone: 'danger', to: '/live-tracking-map-operations-console' },
          { label: 'Bookings to plan', value: confirmedRows.length, icon: 'fa-drafting-compass', tone: 'warning', to: '/dispatch-planning-resource-allocation' },
          { label: 'On-time performance', value: m?.onTimePct != null ? `${m.onTimePct}%` : '—', icon: 'fa-clock', tone: 'success', to: '/fleet-performance-operations-ppa-reports' },
          { label: 'Avg fleet utilization', value: `${avg}%`, icon: 'fa-gauge-high', tone: 'info', to: '/fleet-scheduler-captain-gantt-timeline' },
        ],
        chart: util.length && {
          title: 'Fleet utilization by tug',
          bars: util.slice(0, 8).map((u, i) => {
            const pct = Math.round(pick(u, 'utilizationPct') || 0);
            return {
              label: pick(u, 'tugName', 'tug_name', 'name', 'tug') || `Tug ${i + 1}`,
              value: `${pct}%`,
              pct,
              tone: pct >= 80 ? 'danger' : pct >= 50 ? 'success' : 'info',
            };
          }),
        },
        table: {
          title: 'Confirmed bookings to plan',
          to: '/dispatch-planning-resource-allocation',
          columns: [
            { key: 'ref', label: 'Booking' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'port', label: 'Port' },
            { key: 'movement', label: 'Movement' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: confirmedRows.slice(0, 6).map((b) => ({
            ref: pick(b, 'bookingRef', 'booking_ref', 'id') || '—',
            vessel: pick(b, 'vesselName', 'vessel_name') || '—',
            port: pick(b, 'portName', 'port_name') || '—',
            movement: pick(b, 'movementType', 'movement_type') || '—',
            status: pick(b, 'status') || '—',
          })),
          empty: 'No confirmed bookings waiting to be planned.',
        },
      };
    },
  },

  [ROLES.FINANCE]: {
    accent: 'success',
    blurb: 'Billing, SAP and commercial performance.',
    async load() {
      const [drafts, sap, comm] = await Promise.all([
        settle(api.listInvoiceDrafts?.()),
        settle(api.getSapErrors?.()),
        settle(api.getCommercialDashboard?.()),
      ]);
      const draftRows = arr(drafts);
      const trend = arr(comm?.trend);
      const maxRev = Math.max(1, ...trend.map((t) => Number(t.revenue) || 0));
      return {
        kpis: [
          { label: 'Invoice drafts to approve', value: draftRows.length, icon: 'fa-receipt', tone: 'warning', to: '/invoicing-draft-billing-approver' },
          { label: 'SAP sync exceptions', value: count(arr(sap)), icon: 'fa-network-wired', tone: 'danger', to: '/sap-s4hana-integration-queue' },
          { label: 'Revenue (period)', value: comm?.totals ? revenueText(comm.totals) : '—', icon: 'fa-chart-line', tone: 'success', to: '/commercial-revenues-costs-dashboard' },
          { label: 'Net margin', value: comm?.totals?.marginPct != null ? `${comm.totals.marginPct}%` : '—', icon: 'fa-percent', tone: 'info', to: '/commercial-revenues-costs-dashboard' },
        ],
        chart: trend.length && {
          title: 'Revenue trend',
          bars: trend.slice(-8).map((t) => {
            const rev = Number(t.revenue) || 0;
            return {
              label: String(pick(t, 'monthLabel', 'month', 'label') || ''),
              value: comm.totals?.currency === null ? rev.toLocaleString('en-US') : money(rev, comm.totals?.currency || 'USD', { decimals: 0 }),
              pct: Math.round((rev / maxRev) * 100),
              tone: 'success',
            };
          }),
        },
        table: {
          title: 'Invoice drafts awaiting approval',
          to: '/invoicing-draft-billing-approver',
          columns: [
            { key: 'ref', label: 'Draft / Booking' },
            { key: 'customer', label: 'Customer' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: draftRows.slice(0, 6).map((d) => ({
            ref: pick(d, 'bookingRef', 'booking_ref', 'draftId', 'id') || '—',
            customer: pick(d, 'customerName', 'customer_name') || '—',
            vessel: pick(d, 'vesselName', 'vessel_name') || '—',
            status: pick(d, 'status') || '—',
          })),
          empty: 'No invoice drafts awaiting approval.',
        },
      };
    },
  },

  [ROLES.TARIFF]: {
    accent: 'info',
    blurb: 'Tariffs, rate cards and allocation rules.',
    async load() {
      const [cards, rules] = await Promise.all([
        settle(api.listRateCards?.()),
        settle(api.listAllocationRules?.()),
      ]);
      const cardRows = arr(cards);
      return {
        kpis: [
          { label: 'Rate cards', value: cardRows.length, icon: 'fa-file-invoice-dollar', tone: 'primary', to: '/tariff-rate-cards-version-manager' },
          { label: 'Allocation rules', value: count(arr(rules)), icon: 'fa-balance-scale', tone: 'info', to: '/allocation-rules-compulsory-floors' },
        ],
        table: {
          title: 'Rate cards',
          to: '/tariff-rate-cards-version-manager',
          columns: [
            { key: 'name', label: 'Rate card' },
            { key: 'version', label: 'Version' },
            { key: 'rate', label: 'Base rate', align: 'end' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: cardRows.slice(0, 6).map((c) => ({
            name: pick(c, 'name') || '—',
            version: pick(c, 'version', 'ver') ?? '—',
            rate: pick(c, 'baseHourlyRate', 'base_hourly_rate', 'baseRate') != null ? money(pick(c, 'baseHourlyRate', 'base_hourly_rate', 'baseRate'), pick(c, 'currency') || 'USD') : '—',
            status: pick(c, 'status') || (pick(c, 'activeTo', 'active_to') ? 'Versioned' : 'Active'),
          })),
          empty: 'No rate cards configured.',
        },
      };
    },
  },

  [ROLES.BOOKING]: {
    accent: 'primary',
    blurb: 'Assisted bookings and order intake.',
    async load() {
      const [queue, bookings] = await Promise.all([
        settle(api.getAssistedQueue?.()),
        settle(api.listBookings?.()),
      ]);
      const queueRows = arr(queue);
      return {
        kpis: [
          { label: 'AI drafts to review', value: queueRows.length, icon: 'fa-headset', tone: 'warning', to: '/order-triaging-assisted-bookings' },
          { label: 'Recent bookings', value: count(arr(bookings)), icon: 'fa-anchor', tone: 'primary', to: '/order-triaging-assisted-bookings' },
        ],
        table: {
          title: 'Assisted-booking drafts to review',
          to: '/order-triaging-assisted-bookings',
          columns: [
            { key: 'ref', label: 'Source / Ref' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: queueRows.slice(0, 6).map((q) => ({
            ref: pick(q, 'subject', 'channel', 'source', 'id') || '—',
            vessel: pick(q, 'vesselName', 'vessel_name') || '—',
            status: pick(q, 'status') || 'Draft',
          })),
          empty: 'No assisted-booking drafts to review.',
        },
      };
    },
  },

  [ROLES.AFFILIATE]: {
    accent: 'info',
    blurb: 'Subcontracting offers and your jobs.',
    async load() {
      const [offers, jobs] = await Promise.all([
        settle(api.getBroadcastOffers?.()),
        settle(api.getMyBroadcastJobs?.()),
      ]);
      const offerRows = arr(offers);
      return {
        kpis: [
          { label: 'Open subcontracting offers', value: offerRows.length, icon: 'fa-hands', tone: 'warning', to: '/affiliate-broadcasts-job-review' },
          { label: 'My subcontracted jobs', value: count(arr(jobs)), icon: 'fa-ship', tone: 'primary', to: '/affiliate-portal-capacity-register' },
        ],
        table: {
          title: 'Open subcontracting offers',
          to: '/affiliate-broadcasts-job-review',
          columns: [
            { key: 'ref', label: 'Broadcast' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'port', label: 'Port' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: offerRows.slice(0, 6).map((o) => ({
            ref: pick(o, 'broadcastId', 'broadcast_id', 'id') || '—',
            vessel: pick(o, 'vesselName', 'vessel_name') || '—',
            port: pick(o, 'portName', 'port_name') || '—',
            status: pick(o, 'status') || 'Open',
          })),
          empty: 'No open subcontracting offers.',
        },
      };
    },
  },

  [ROLES.PILOT]: {
    accent: 'primary',
    blurb: 'Open pilotage requests and your assignments.',
    async load() {
      const [open, mine] = await Promise.all([
        settle(api.getPilotMarketplace?.()),
        settle(api.getMyPilotAssignments?.()),
      ]);
      const openRows = arr(open);
      return {
        kpis: [
          { label: 'Open pilotage requests', value: openRows.length, icon: 'fa-bullhorn', tone: 'warning', to: '/pilot-assignments-market-coordinator' },
          { label: 'My active assignments', value: count(arr(mine)), icon: 'fa-user-tie', tone: 'primary', to: '/pilot-assignments-market-coordinator' },
        ],
        table: {
          title: 'Open pilotage requests',
          to: '/pilot-assignments-market-coordinator',
          columns: [
            { key: 'ref', label: 'Booking' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'movement', label: 'Movement' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: openRows.slice(0, 6).map((o) => ({
            ref: pick(o, 'bookingRef', 'booking_ref', 'id') || '—',
            vessel: pick(o, 'vesselName', 'vessel_name') || '—',
            movement: pick(o, 'movementType', 'movement_type') || '—',
            status: pick(o, 'status') || 'Open',
          })),
          empty: 'No open pilotage requests right now.',
        },
      };
    },
  },

  [ROLES.CAPTAIN]: {
    accent: 'primary',
    blurb: 'Your active tug ticket and execution.',
    async load() {
      const ticket = await settle(api.getActiveTicket?.());
      return {
        kpis: [
          { label: 'Active dispatch ticket', value: pick(ticket, 'bookingRef', 'booking_ref') || 'None', icon: 'fa-id-badge', tone: 'primary', to: '/captain-dispatch-ticket-execution-clock-pwa' },
          { label: 'Vessel', value: pick(ticket, 'vesselName', 'vessel_name') || '—', icon: 'fa-ship', tone: 'info', to: '/captain-dispatch-ticket-execution-clock-pwa' },
        ],
      };
    },
  },

  [ROLES.MAINT]: {
    accent: 'warning',
    blurb: 'Tug maintenance & bunkering downtime.',
    async load() {
      const [tugs, timeline] = await Promise.all([
        settle(api.listTugs?.()),
        settle(api.getTugTimeline?.()),
      ]);
      const tl = arr(timeline);
      const withDowntime = tl.filter((r) => (r.maintenanceWindows || []).length > 0);
      const conflicts = tl.filter((r) => r.hasConflict);
      return {
        kpis: [
          { label: 'Tugs with downtime windows', value: withDowntime.length, icon: 'fa-wrench', tone: 'warning', to: '/maintenance-bunkering-scheduler' },
          { label: 'Scheduling conflicts', value: conflicts.length, icon: 'fa-triangle-exclamation', tone: 'danger', to: '/maintenance-bunkering-scheduler' },
          { label: 'Fleet size', value: count(arr(tugs)), icon: 'fa-ship', tone: 'primary', to: '/maintenance-bunkering-scheduler' },
        ],
        table: {
          title: 'Tug downtime board',
          to: '/maintenance-bunkering-scheduler',
          columns: [
            { key: 'tug', label: 'Tug' },
            { key: 'windows', label: 'Windows', align: 'end' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: tl.slice(0, 6).map((r) => ({
            tug: r.name,
            windows: (r.maintenanceWindows || []).length,
            status: r.hasConflict ? 'Conflict' : (r.maintenanceWindows || []).length ? 'Downtime' : 'Available',
          })),
          empty: 'No tugs.',
        },
      };
    },
  },

  [ROLES.CUSTOMER]: {
    accent: 'secondary',
    blurb: 'Your bookings and saved vessels.',
    async load() {
      const [bookings, favs] = await Promise.all([
        settle(api.listBookings?.()),
        settle(api.listFavoriteVessels?.()),
      ]);
      const bookingRows = arr(bookings);
      return {
        kpis: [
          { label: 'My bookings', value: bookingRows.length, icon: 'fa-anchor', tone: 'primary', to: '/customer-booking-form-live-dashboard' },
          { label: 'Favorite vessels', value: count(arr(favs)), icon: 'fa-star', tone: 'warning', to: '/customer-favorite-vessels-preferences' },
        ],
        table: {
          title: 'My recent bookings',
          to: '/customer-booking-form-live-dashboard',
          columns: [
            { key: 'ref', label: 'Booking' },
            { key: 'vessel', label: 'Vessel' },
            { key: 'port', label: 'Port' },
            { key: 'status', label: 'Status', align: 'end' },
          ],
          rows: bookingRows.slice(0, 6).map((b) => ({
            ref: pick(b, 'bookingRef', 'booking_ref', 'id') || '—',
            vessel: pick(b, 'vesselName', 'vessel_name') || '—',
            port: pick(b, 'portName', 'port_name') || '—',
            status: pick(b, 'status') || '—',
          })),
          empty: 'You have no bookings yet.',
        },
      };
    },
  },
};

// Priority order when a user has multiple roles (rare).
const PRIORITY = [
  ROLES.ADMIN, ROLES.DISPATCHER, ROLES.FINANCE, ROLES.TARIFF,
  ROLES.BOOKING, ROLES.AFFILIATE, ROLES.PILOT, ROLES.CAPTAIN, ROLES.MAINT, ROLES.CUSTOMER,
];

function MiniBarChart({ chart, accent }) {
  return (
    <div className="card border-0 shadow-sm h-100" data-testid="dashboard-chart">
      <div className="card-header bg-white pt-3 border-bottom-0">
        <h6 className="mb-0 text-secondary">
          <i className="fas fa-chart-bar me-2" aria-hidden="true" />
          {chart.title}
        </h6>
      </div>
      <div className="card-body pt-2">
        {chart.bars.map((b) => (
          <div className="mb-3" key={b.label}>
            <div className="d-flex justify-content-between small mb-1">
              <span className="text-truncate text-secondary" style={{ maxWidth: '60%' }}>{b.label}</span>
              <span className={`fw-semibold text-${b.tone || accent}`}>{b.value}</span>
            </div>
            <div className="progress" style={{ height: '8px' }} role="progressbar" aria-valuenow={b.pct} aria-valuemin={0} aria-valuemax={100}>
              <div className={`progress-bar bg-${b.tone || accent}`} style={{ width: `${Math.max(2, Math.min(100, b.pct))}%` }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function QueueTable({ table, accent }) {
  return (
    <div className="card border-0 shadow-sm h-100" data-testid="dashboard-table">
      <div className="card-header bg-white pt-3 border-bottom-0 d-flex justify-content-between align-items-center">
        <h6 className="mb-0 text-secondary">
          <i className="fas fa-list-ul me-2" aria-hidden="true" />
          {table.title}
        </h6>
        {table.to && (
          <Link to={table.to} className={`small text-${accent} text-decoration-none fw-semibold`}>
            View all <i className="fas fa-arrow-right ms-1" aria-hidden="true" />
          </Link>
        )}
      </div>
      <div className="card-body p-0">
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0 small">
            <thead className="bg-light text-secondary text-uppercase">
              <tr>
                {table.columns.map((c) => (
                  <th key={c.key} className={c.align === 'end' ? 'text-end' : ''}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.length === 0 ? (
                <tr>
                  <td colSpan={table.columns.length} className="text-center text-muted py-4">{table.empty}</td>
                </tr>
              ) : (
                table.rows.map((row, i) => (
                  <tr key={i} data-testid="dashboard-table-row">
                    {table.columns.map((c) => (
                      <td key={c.key} className={c.align === 'end' ? 'text-end' : ''}>{row[c.key]}</td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const roles = useMemo(() => user?.roles || [], [user]);
  const primary = useMemo(() => PRIORITY.find((r) => roles.includes(r)) || null, [roles]);

  const [data, setData] = useState({ kpis: [], chart: null, table: null });
  const [loading, setLoading] = useState(true);

  const quickLinks = useMemo(
    () => menuLeaves.filter((l) => l.slug !== 'dashboard' && canAccessLeaf(l, { roles })),
    [roles]
  );

  useEffect(() => {
    let alive = true;
    const cfg = primary && ROLE_DASH[primary];
    if (!cfg) {
      setData({ kpis: [], chart: null, table: null });
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    cfg.load()
      .then((res) => alive && setData({ kpis: res?.kpis || [], chart: res?.chart || null, table: res?.table || null }))
      .catch(() => alive && setData({ kpis: [], chart: null, table: null }))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [primary]);

  const cfg = primary ? ROLE_DASH[primary] : null;
  const accent = cfg?.accent || 'primary';
  const { kpis, chart, table } = data;

  return (
    <div className="container-fluid py-4" data-testid="dashboard-page">
      <div className="row mb-3">
        <div className="col-12">
          <div className="bg-white p-4 rounded shadow-sm d-flex justify-content-between align-items-center flex-wrap gap-2">
            <div>
              <h3 className={`text-${accent} mb-1`}>
                <i className="fas fa-anchor me-2" aria-hidden="true" />
                Welcome{user?.name ? `, ${user.name}` : ''}
              </h3>
              <p className="text-muted mb-0 small">{cfg?.blurb || 'TugOS operations platform.'}</p>
            </div>
            {primary && (
              <span className={`badge bg-${accent} p-2`} data-testid="dashboard-role">{primary}</span>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-center text-muted py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          {cfg && kpis.length > 0 && (
            <div className="row g-3 mb-4" data-testid="dashboard-kpis">
              {kpis.map((k) => (
                <div className="col-6 col-lg-3" key={k.label}>
                  <Link to={k.to || '#'} className="text-decoration-none" data-testid="dashboard-kpi">
                    <div className="card border-0 shadow-sm h-100">
                      <div className="card-body d-flex align-items-center">
                        <span className={`badge bg-${k.tone || accent} bg-opacity-10 text-${k.tone || accent} p-3 me-3`}>
                          <i className={`fas ${k.icon} fa-lg`} aria-hidden="true" />
                        </span>
                        <div>
                          <div className={`h4 mb-0 text-${k.tone || accent}`}>{k.value}</div>
                          <div className="text-muted small">{k.label}</div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </div>
              ))}
            </div>
          )}

          {/* Chart + queue table */}
          {(chart || table) && (
            <div className="row g-3 mb-4">
              {chart && (
                <div className={table ? 'col-lg-5' : 'col-12'}>
                  <MiniBarChart chart={chart} accent={accent} />
                </div>
              )}
              {table && (
                <div className={chart ? 'col-lg-7' : 'col-12'}>
                  <QueueTable table={table} accent={accent} />
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Quick links to the role's pages */}
      <h6 className="text-secondary text-uppercase small mb-2" style={{ letterSpacing: '0.06em' }}>
        Quick access
      </h6>
      <div className="row g-3">
        {quickLinks.map((l) => (
          <div className="col-6 col-md-4 col-xl-3" key={l.slug}>
            <Link to={`/${l.slug}`} className="text-decoration-none" data-testid="dashboard-link">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body d-flex align-items-center py-3">
                  <span className={`badge bg-${accent} bg-opacity-10 text-${accent} p-2 me-3`}>
                    <i className={`${l.icon} fa-lg`} aria-hidden="true" />
                  </span>
                  <span className="fw-semibold text-dark">{l.label}</span>
                </div>
              </div>
            </Link>
          </div>
        ))}
        {!quickLinks.length && (
          <div className="col-12 text-muted small">No pages available for your role.</div>
        )}
      </div>
    </div>
  );
}
